// @ts-strict-ignore
import {
  Box,
  ErrorBoundary,
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import { useState } from 'react';
import styled from 'styled-components';
import type { Document } from '~/entities/types';
import { DocReviewActions } from './components/DocReviewActions';
import { DocReviewBody } from './components/DocReviewBody';
import { DocReviewHeader } from './components/DocReviewHeader';
import { DocReviewNavigator } from './components/DocReviewNavigator';
import { DocReviewNotes } from './components/DocReviewNotes';
import type { EntitiesMap, SuppliedDataValue } from './DocumentReviewModal';

interface Props {
  documents: Document[];
  entities?: EntitiesMap;
  hideReviewActions?: boolean;
  onRenameSuccess?: (args: { document_token: string; entity_token: string }) => void;
  onReviewSuccess?: (args: { document_token: string; entity_token: string }) => void;
  onUploadClick?: () => void;
  uploadPopoverWrapper?: (button: React.ReactNode) => React.ReactNode;
  shouldPinReviewActions?: boolean;
  suppliedDataValues?: SuppliedDataValue[];
}

const AddButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  height: 40,
  width: 40,
  flexShrink: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'var(--cl-gray-300)',
  color: 'var(--cl-gray-700)',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'var(--cl-gray-400)',
  },
  '&:focus': {
    outline: 'none',
    boxShadow: 'none',
  },
  '&:focus-visible': {
    outline: 'none',
    boxShadow: '0px 0px 0px 1px var(--cl-juicyBlue-600), 0px 0px 0px 3px lch(from var(--cl-juicyBlue-600) l c h / .2)',
  },
});

export const DocumentReviewer = ({
  documents,
  entities,
  hideReviewActions = false,
  onRenameSuccess,
  onReviewSuccess,
  onUploadClick,
  uploadPopoverWrapper,
  shouldPinReviewActions = false,
  suppliedDataValues,
}: Props) => {
  const [activeDocToken, setActiveDocToken] = useState(documents.at(0)?.document_token);

  const activeDocument = documents.find(data => data.document_token === activeDocToken);

  return (
    <ErrorBoundary>
      <Box direction="row" alignItems="flex-start" justifyContent="flex-start" gap={2} width="100%">
        {onUploadClick && (() => {
          const addButton = (
            <AddButton
              type="button"
              aria-label="Upload document"
              title="Upload document"
              onClick={onUploadClick}
              data-pendo-anchor="document-review-modal-upload"
            >
              <Icon icon={IconName.Add} size="large" />
            </AddButton>
          );
          return uploadPopoverWrapper ? uploadPopoverWrapper(addButton) : addButton;
        })()}
        <Box direction="column" style={{ flexGrow: 1, minWidth: 0 }}>
          <DocReviewNavigator
            documents={documents}
            activeDocToken={activeDocToken}
            setActiveDocToken={setActiveDocToken}
          />
        </Box>
      </Box>
      { activeDocument ? (
        <>
          <DocReviewHeader
            key={`header-${activeDocument?.document_token}`}
            document={activeDocument}
            entities={entities}
            onRenameDocSuccess={() => {
              onRenameSuccess?.({
                document_token: activeDocToken,
                entity_token: activeDocument.entity_token,
              });
            }}
          />
          <DocReviewBody
            key={`body-${activeDocument?.document_token}`}
            document={activeDocument}
            suppliedDataValues={suppliedDataValues}
          />
          <DocReviewNotes
            document={activeDocument}
            onNoteAddSuccess={() => {
              onReviewSuccess?.({
                document_token: activeDocToken,
                entity_token: activeDocument.entity_token,
              });
            }}
          />
          <DocReviewActions
            key={`actions-${activeDocument?.document_token}`}
            document={activeDocument}
            hideReviewActions={hideReviewActions}
            onReviewSuccess={() => {
              onReviewSuccess?.({
                document_token: activeDocToken,
                entity_token: activeDocument.entity_token,
              });
            }}
            shouldPinReviewActions={shouldPinReviewActions}
          />
        </>
      ) : (
        <Text>
          Unable to load the selected document. Please try selecting another document.
        </Text>
      )}
    </ErrorBoundary>
  );
};
